import {
  type PointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const EDGE = 8;

/** Controls and the scrolling details keep their own press (scroll, select
 * text); a drag starts on the pinned header, padding or footer. */
const INTERACTIVE =
  "button, a, input, select, textarea, summary, [role='button'], [data-call-context-scroll]";

interface Grab {
  pointerId: number;
  clientX: number;
  clientY: number;
  base: Offset;
  /** Travel still available before the sheet leaves the window. */
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface Offset {
  x: number;
  y: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * The offset is a transform, so sibling slips don't move. The bottom-anchored
 * column shifts a sheet's origin as slips ring or clear, so the offset is pulled
 * back inside the window after every render and on resize.
 */
export const useDragOffset = () => {
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const sheet = useRef<HTMLDivElement | null>(null);
  const grab = useRef<Grab | null>(null);

  const settleInsideWindow = useCallback(() => {
    const el = sheet.current;
    // Mid-drag the pointer handler is already clamping against live bounds.
    if (!el || grab.current) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    setOffset((held) => {
      // Right and bottom first, then left and top, so a sheet taller or wider
      // than the window keeps the corner a person reads from on screen.
      let dx = Math.min(0, window.innerWidth - EDGE - rect.right);
      let dy = Math.min(0, window.innerHeight - EDGE - rect.bottom);
      dx += Math.max(0, EDGE - (rect.left + dx));
      dy += Math.max(0, EDGE - (rect.top + dy));
      // Same object when nothing moved: React bails out rather than looping.
      return dx === 0 && dy === 0 ? held : { x: held.x + dx, y: held.y + dy };
    });
  }, []);

  // No dependency array: the sheet's origin moves with its siblings, and this
  // is the render those changes land in.
  useLayoutEffect(settleInsideWindow);

  useEffect(() => {
    window.addEventListener("resize", settleInsideWindow);
    return () => window.removeEventListener("resize", settleInsideWindow);
  }, [settleInsideWindow]);

  const end = (event: PointerEvent<HTMLElement>) => {
    if (grab.current?.pointerId !== event.pointerId) return;
    grab.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return {
    dragging,
    /** Dragged clear of the corner, so it should paint over what it covers. */
    moved: offset.x !== 0 || offset.y !== 0,
    dragProps: {
      ref: sheet,
      style: {
        transform:
          offset.x || offset.y
            ? `translate3d(${offset.x}px, ${offset.y}px, 0)`
            : undefined,
        // Without this a touch drag scrolls the page instead of the sheet.
        touchAction: "none" as const,
      },
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (event.button !== 0) return;
        if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
        const rect = event.currentTarget.getBoundingClientRect();
        grab.current = {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
          base: offset,
          minX: EDGE - rect.left,
          maxX: window.innerWidth - EDGE - rect.right,
          minY: EDGE - rect.top,
          maxY: window.innerHeight - EDGE - rect.bottom,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
      },
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        const held = grab.current;
        if (held?.pointerId !== event.pointerId) return;
        setOffset({
          x:
            held.base.x +
            clamp(event.clientX - held.clientX, held.minX, held.maxX),
          y:
            held.base.y +
            clamp(event.clientY - held.clientY, held.minY, held.maxY),
        });
      },
      onPointerUp: end,
      onPointerCancel: end,
    },
  };
};
